/**
 * Utility functions for store operations
 */

import { syncStorage, initStorage, migrateFromLocalStorage } from './indexedDBStorage';

/**
 * Generate a UUID v4
 */
export function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get current ISO timestamp
 */
export function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Debounce function - delays execution until after wait milliseconds have elapsed
 * @param func - Function to debounce
 * @param wait - Milliseconds to wait
 * @returns Debounced function with flush method
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T & { flush: () => void; cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: any[] | null = null;

  const debounced = function (this: any, ...args: any[]) {
    lastArgs = args;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func.apply(this, lastArgs!);
      timeoutId = null;
      lastArgs = null;
    }, wait);
  } as T & { flush: () => void; cancel: () => void };

  // Immediately execute pending call
  debounced.flush = function (this: any) {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      func.apply(this, lastArgs);
      timeoutId = null;
      lastArgs = null;
    }
  };

  // Cancel pending call
  debounced.cancel = function () {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
      lastArgs = null;
    }
  };

  return debounced;
}

/**
 * Storage key prefix to avoid conflicts with parent applications
 * All keys will be prefixed with this to ensure isolation
 */
const STORAGE_PREFIX = 'map-editor';

/**
 * Add prefix to storage key for scoping
 */
function getScopedKey(key: string): string {
  return `${STORAGE_PREFIX}-${key}`;
}

/**
 * IndexedDB-based storage with sync interface for backward compatibility
 * Supports large datasets (2000+ parking spots) without localStorage 5MB limit
 * All keys are automatically scoped with 'map-editor-' prefix to prevent conflicts
 */
export const storage = {
  get<T>(key: string, defaultValue: T): T {
    return syncStorage.get(getScopedKey(key), defaultValue);
  },

  set<T>(key: string, value: T): boolean {
    return syncStorage.set(getScopedKey(key), value);
  },

  remove(key: string): boolean {
    return syncStorage.remove(getScopedKey(key));
  },
};

// Re-export for app initialization
export { initStorage, migrateFromLocalStorage };
